import { FC, useMemo } from 'react';
import { useLocation } from 'react-router';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import {
  TextField,
  IconButton,
  Box,
  Typography,
  useTheme,
  Stack,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  useMediaQuery
} from '@mui/material';
import ModalAddSocialProfile from 'src/components/ModalAddSocialProfile';
import {
  IObject,
  TObjectFilter,
  TStreamTypeFilter
} from 'src/models/objectManagement';
import ModalAddKeywordAndHashtag from 'src/components/ModalAddKeywordAndHashtag';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  reducerUpdateObjectType,
  reducerUpdateSelectedStreamType
} from 'src/features/objectManagement';
import ModalAddPostLink from 'src/components/ModalAddPostLink';
interface Props {
  objectCount: any;
  onSearch: (keyword: string) => void;
  objectList: Array<IObject>;
}

export interface IOption {
  id: string;
  label: string;
  value: string;
}

export const objectTypeOptions: IOption[] = [
  {
    id: 'all',
    label: 'All data source',
    value: 'all'
  },
  {
    id: 'object',
    label: 'Object',
    value: 'object'
  },
  {
    id: 'entity',
    label: 'Entity based',
    value: 'entity'
  }
];

export const streamTypeOptions: IOption[] = [
  {
    id: 'all',
    label: 'All object type',
    value: 'all'
  },
  {
    id: 'keyword',
    label: 'Keyword',
    value: 'keyword'
  },
  {
    id: 'hashtag',
    label: 'Hashtag',
    value: 'hashtag'
  },
  {
    id: 'account',
    label: 'Account',
    value: 'account'
  },
  {
    id: 'post_link',
    label: 'Post Link',
    value: 'post_link'
  }
];

const HeaderObjects: FC<Props> = ({ objectCount, onSearch, objectList }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const {
    account,
    entityAccount,
    keyword,
    entityKeyword,
    accountQuota,
    keywordQuota
  } = objectCount;
  const { objectType, selectedStreamType } = useAppSelector(
    (state) => state.objectManagement
  );
  const title = pathname.split('/')[2];
  const isEcommerce = title === 'ecommerce';

  // const objectListSocialProfile = useMemo((): Array<IObject> => {
  //   return objectList.filter((object) => object.streamType === 'account');
  // }, [objectList]);

  // const objectListKeywordAndHashtag = useMemo((): Array<IObject> => {
  //   return objectList.filter((object) => object.streamType === 'keyword');
  // }, [objectList]);

  const handleSearch = function (e: any) {
    const keyword = e.target.value;
    onSearch(keyword);
  };

  const handleChangeObjectType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(reducerUpdateObjectType(selectedValue as TObjectFilter));
  };

  const handleChangeStreamType = (
    event: SelectChangeEvent<string>,
    child: React.ReactNode
  ) => {
    const selectedValue = event.target.value;
    dispatch(
      reducerUpdateSelectedStreamType(selectedValue as TStreamTypeFilter)
    );
  };

  const matchedMediaUpMd = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <>
      <Stack
        direction={'row'}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction="row" gap={2}>
          <TextField
            size="small"
            sx={{ flexGrow: 1, minWidth: { md: 300 } }}
            InputProps={{
              startAdornment: (
                <IconButton size="small">
                  <SearchTwoToneIcon />
                </IconButton>
              )
            }}
            placeholder={`Search ${title}`}
            onChange={handleSearch}
          />
          {!isEcommerce && (
            <Stack
              direction={'row'}
              spacing={2}
              width={{ xs: '100%', md: '300px' }}
            >
              <Select
                id="objectType"
                name="objectType"
                value={objectType}
                onChange={handleChangeObjectType}
                sx={{ width: '100%' }}
              >
                <MenuItem key="placeholder" id="placeholder" value="" disabled>
                  Data Source
                </MenuItem>
                {objectTypeOptions.map(({ id, value, label }) => (
                  <MenuItem key={id} id={id} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <Select
                id="streamType"
                name="streamType"
                value={selectedStreamType}
                onChange={handleChangeStreamType}
                sx={{ width: '100%' }}
              >
                <MenuItem key="placeholder" id="placeholder" value="" disabled>
                  Object Type
                </MenuItem>
                {streamTypeOptions.map(({ id, value, label }) => (
                  <MenuItem key={id} id={id} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          mt={{ xs: 1, md: 0 }}
          spacing={theme.spacing(2)}
        >
          {!isEcommerce && (
            <ModalAddPostLink
              title="Post Link"
              buttonLabel="Post Link"
              buttonProps={{
                variant: 'contained',
                sx: {
                  backgroundColor: theme.colors.primary.dark,
                  borderRadius: theme.spacing(1)
                }
              }}
            />
          )}
          <ModalAddSocialProfile
            title={isEcommerce ? 'Store Account' : 'Social Profile'}
            // objectList={objectListSocialProfile}
            buttonLabel={isEcommerce ? 'Store Account' : 'Social Profile'}
            buttonProps={{
              variant: 'contained',
              sx: {
                backgroundColor: theme.colors.primary.dark,
                borderRadius: theme.spacing(1)
              }
            }}
          />
          <ModalAddKeywordAndHashtag
            title={isEcommerce ? 'Keyword' : 'Keyword & Hashtag'}
            buttonLabel={isEcommerce ? 'Keyword' : 'Keyword & Hashtag'}
            buttonProps={{
              variant: 'contained',
              sx: {
                backgroundColor: theme.colors.primary.dark,
                borderRadius: theme.spacing(1)
              }
            }}
            streamType={isEcommerce ? 'keyword' : null}
          />
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap={'wrap'} gap={2}>
        <>
          {!isEcommerce && (
            <Box sx={{ display: 'flex', marginTop: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 14 }}>
                Account{' '}
                <Typography
                  component="span"
                  variant="h4"
                  color={theme.palette.primary.main}
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  {account}/{accountQuota}
                </Typography>
              </Typography>
              <Typography
                variant="h4"
                sx={{ marginLeft: 2, fontWeight: 600, fontSize: 14 }}
              >
                {isEcommerce ? 'Keyword' : 'Keyword & Hashtag'}{' '}
                <Typography
                  component="span"
                  variant="h4"
                  color={theme.palette.primary.main}
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  {keyword}/{keywordQuota}
                </Typography>
              </Typography>
            </Box>
          )}
          {!isEcommerce && (
            <>
              <Divider
                orientation={'vertical'}
                variant="middle"
                sx={{
                  border: '1px solid',
                  color: theme.palette.primary.main,
                  marginY: 0,
                  marginTop: '14px'
                }}
                flexItem
              />
              <Box sx={{ display: 'flex', marginTop: { xs: 0, md: 2 } }}>
                <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 14 }}>
                  Entity Account{' '}
                  <Typography
                    component="span"
                    variant="h4"
                    color={theme.palette.primary.main}
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    {entityAccount}
                  </Typography>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ marginLeft: 2, fontWeight: 600, fontSize: 14 }}
                >
                  Entity Keyword & Hashtag{' '}
                  <Typography
                    component="span"
                    variant="h4"
                    color={theme.palette.primary.main}
                    sx={{ fontWeight: 600, fontSize: 14 }}
                  >
                    {entityKeyword}
                  </Typography>
                </Typography>
              </Box>
            </>
          )}
        </>
      </Stack>
    </>
  );
};

export default HeaderObjects;
